import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Loader2, Camera, Trash2, X, Upload } from "lucide-react";
import { toast } from "sonner";
import { showError } from "../lib/errorHandler";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { key: "part", label: "Peça", color: "bg-blue-100 text-blue-700 border-blue-300" },
  { key: "mold", label: "Molde", color: "bg-indigo-100 text-indigo-700 border-indigo-300" },
  { key: "defect", label: "Defeito", color: "bg-rose-100 text-rose-700 border-rose-300" },
  { key: "packaging", label: "Embalagem", color: "bg-amber-100 text-amber-700 border-amber-300" },
  { key: "final", label: "Produto Final", color: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  { key: "other", label: "Outros", color: "bg-slate-100 text-slate-700 border-slate-300" },
];

export default function OrderPhotos({ orderId }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("part");
  const [viewer, setViewer] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/orders/${orderId}/photos`);
      setPhotos(r.data || []);
    } catch (e) { showError(e, "Erro"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (orderId) load(); /* eslint-disable-next-line */ }, [orderId]);

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Imagem muito grande (max 5MB)"); return; }
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          await axios.post(`${API}/orders/${orderId}/photos`, {
            order_id: orderId,
            category: selectedCategory,
            image_url: reader.result,
          });
          toast.success("Foto guardada");
          await load();
        } catch (err) { showError(err, "Erro"); }
        finally { setUploading(false); }
      };
      reader.readAsDataURL(file);
    } catch (e) {
      setUploading(false);
      showError(e, "Erro");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Apagar esta foto?")) return;
    try { await axios.delete(`${API}/orders/photos/${id}`); toast.success("Apagada"); load(); }
    catch (e) { showError(e, "Erro"); }
  };

  const byCategory = photos.reduce((acc, p) => { (acc[p.category] = acc[p.category] || []).push(p); return acc; }, {});

  return (
    <div className="p-4 space-y-3" data-testid="order-photos">
      {/* Upload */}
      <div className="border-2 border-dashed border-slate-300 rounded-lg p-3 bg-slate-50">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-bold uppercase text-slate-500">Adicionar foto:</span>
          <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} className="text-xs border rounded px-2 py-1" data-testid="photo-category">
            {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="file" accept="image/*" capture="environment" onChange={onFileChange} className="hidden" disabled={uploading} data-testid="photo-file-input" />
          <Button asChild size="sm" disabled={uploading}>
            <span>
              {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
              Escolher / Tirar foto
            </span>
          </Button>
        </label>
      </div>

      {loading ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : (
        <>
          {photos.length === 0 && <div className="text-center text-xs text-muted-foreground py-4">Ainda sem fotos</div>}
          {CATEGORIES.map(cat => {
            const list = byCategory[cat.key] || [];
            if (list.length === 0) return null;
            return (
              <div key={cat.key}>
                <div className={`inline-flex items-center text-[10px] font-bold uppercase px-2 py-0.5 rounded border mb-2 ${cat.color}`}>{cat.label} ({list.length})</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {list.map(p => (
                    <div key={p.id} className="relative group aspect-square bg-slate-100 rounded overflow-hidden border">
                      <img src={p.image_url} alt={cat.label} className="w-full h-full object-cover cursor-pointer" onClick={() => setViewer(p)} data-testid={`photo-${p.id}`} />
                      <button onClick={() => remove(p.id)} className="absolute top-1 right-1 bg-white/90 hover:bg-red-500 hover:text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition">
                        <Trash2 className="h-3 w-3" />
                      </button>
                      <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] px-1 py-0.5 truncate">
                        {p.uploaded_by_name || p.uploaded_by} · {new Date(p.uploaded_at).toLocaleDateString("pt-PT")}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* Viewer modal */}
      {viewer && (
        <div className="fixed inset-0 z-[80] bg-black/90 flex items-center justify-center p-4" onClick={() => setViewer(null)}>
          <button className="absolute top-4 right-4 text-white" onClick={() => setViewer(null)}><X className="h-6 w-6" /></button>
          <img src={viewer.image_url} alt="" className="max-h-[90vh] max-w-full rounded" />
          <div className="absolute bottom-4 left-4 right-4 bg-white/95 rounded px-3 py-2 text-xs text-center">
            <strong>{CATEGORIES.find(c => c.key === viewer.category)?.label}</strong> · {viewer.uploaded_by_name || viewer.uploaded_by} · {new Date(viewer.uploaded_at).toLocaleString("pt-PT")}
            {viewer.notes && <div className="text-slate-600 mt-1">{viewer.notes}</div>}
          </div>
        </div>
      )}
    </div>
  );
}
